import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Bell } from 'lucide-react';
import { useQuery } from '@tanstack/react-query';
import { useAuth } from '@/features/auth/components/AuthContext';
import { supabase } from '@/integrations/supabase/client';
import { NotificationsPanel } from './NotificationsPanel';

/**
 * Notification entry point for the shared header (TopBar), beside the Help button.
 * DISPLAY-ONLY: shows a badge with the count of AGXL voice responses this farmer
 * has NOT heard yet (help_messages.response_audio_path IS NOT NULL AND
 * response_heard = false). Responses are seeded externally; this never writes one.
 */
export const NotificationButton = () => {
  const { t } = useTranslation();
  const { user } = useAuth();
  const [open, setOpen] = useState(false);

  const { data: unheard = 0 } = useQuery({
    queryKey: ['help-response-unheard', user?.id],
    queryFn: async () => {
      if (!user?.id) return 0;
      // RLS already scopes help_messages to this farmer's own rows.
      const { count } = await supabase
        .from('help_messages')
        .select('id', { count: 'exact', head: true })
        .not('response_audio_path', 'is', null)
        .eq('response_heard', false);
      return count ?? 0;
    },
    enabled: !!user?.id,
    refetchInterval: 60_000,
    refetchOnWindowFocus: true,
    staleTime: 30_000,
  });

  if (!user?.id) return null;

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        aria-label={t('notifications.title', 'Notifications')}
        title={t('notifications.title', 'Notifications')}
        className="relative flex items-center justify-center w-10 h-10 rounded-full hover:bg-white/10 active:scale-95 transition-all"
      >
        <Bell className="w-6 h-6 text-white" />
        {unheard > 0 && (
          <span
            className="absolute top-0.5 right-0.5 min-w-[18px] h-[18px] px-1 rounded-full bg-red-500 text-white text-[11px] font-bold leading-none flex items-center justify-center"
            aria-label={t('notifications.newResponse', 'New response')}
          >
            {unheard > 9 ? '9+' : unheard}
          </span>
        )}
      </button>
      <NotificationsPanel isOpen={open} onClose={() => setOpen(false)} />
    </>
  );
};
